import { FC, ReactNode, useEffect } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import { FormDatePicker } from '../../../../../components/date-picker/index';
import { FormInput } from '../../../../../components/input/index';
import RootLoader from '../../../../../components/root-loader/index';
import { Switch } from '../../../../../components/switch/index';
import { SafeHooks, ScenarioHooks, useFormat } from '../../../../../hooks/index';
import { ScenarioType } from '../../../../../types/scenario-modeling.types';
import { Snapshot, SnapshotListItem } from '../../../../../types/snapshot.types';
import { toNumber } from '../../../../../utils/to-number.util';
import { useScenarioStore } from '../../../scenario-modeling.store';
import { LiquidityFormSchema } from '../../liquidity-validation';
import { valuationFormSchema } from '../../scenario-details.validation';
import { ScenarioDropdown } from '../scenario-dropdown/index';
import {
  ClassConversionList,
  CommonGrantItemList,
  SafeItemList,
} from '../scenario-dropdown/items/index';
import {
  ExistingScenarioDetails,
  liquidityScenarioItems,
  LiquidityScenarioKey,
  NewScenarioDetails,
} from './liquidity.types';
import { useLiquidityCreation } from './use-liquidity-creation';

type LiquidityScenarioFormProps = {
  addButtonText: ReactNode | string;
  additionalColumns: (ReactNode | string)[];
  additionalColumnsCount: number;
  columns: (ReactNode | string)[];
  columnsCount: number;
  companyId: string;
  control: Control<LiquidityFormSchema>;
  scenarioId: string;
  scenarioType: ScenarioType;
  setValue: UseFormSetValue<LiquidityFormSchema>;
};

const scenarioFormMap: Record<
  LiquidityScenarioKey,
  (props: LiquidityScenarioFormProps) => ReactNode
> = {
  [LiquidityScenarioKey.SAFES]: (props) => <SafeItemList {...props} />,
  [LiquidityScenarioKey.GRANTS]: (props) => <CommonGrantItemList {...props} />,
  [LiquidityScenarioKey.CLASS_CONVERSION]: (props) => <ClassConversionList {...props} />,
};

export type LiquidityCreationProps = {
  companyId: string;
  control: Control<LiquidityFormSchema>;
  scenarioId: string;
  scenarioType: ScenarioType;
  setValue: UseFormSetValue<LiquidityFormSchema>;
  snapshot?: Snapshot | SnapshotListItem;
};

export const LiquidityCreation: FC<LiquidityCreationProps> = ({
  snapshot,
  control,
  companyId,
  scenarioId,
  setValue,
  scenarioType,
}) => {
  const { format } = useFormat();
  const { safes, totalSafesCount } = SafeHooks.useSafes({
    companyId,
  });
  const { scenarioVestingTasks } = ScenarioHooks.useScenarioVestingTasks({ scenarioId });
  const { scenarioConversionTasks } = ScenarioHooks.useScenarioClassConversionTasks({ scenarioId });

  const { pointerBlockedStyles } = useScenarioStore();

  const { scenario } = ScenarioHooks.useScenario({ scenarioId });

  const { safes: selectedSafes, classConversion } = useWatch<LiquidityFormSchema>({ control });
  const valuation = useWatch({ control, name: 'valuation' });
  const isSelectedAccelerationDate = useWatch({ control, name: 'accelerateVesting' });
  const parsedValuation = valuationFormSchema.safeParse(valuation);
  const isValuationReady = Boolean(
    parsedValuation.success && parsedValuation.data.date && parsedValuation.data.preMoneyValuation,
  );
  const { debounceValuationCallback, isPending } = useLiquidityCreation({
    scenarioId,
    valuation: { ...valuation, accelerateVesting: isSelectedAccelerationDate },
  });

  useEffect(() => {
    debounceValuationCallback({});
  }, [debounceValuationCallback, isSelectedAccelerationDate, valuation]);

  return (
    <>
      {isPending && <RootLoader />}

      <div className="flex w-full min-w-0 flex-col gap-4">
        <div className="flex items-center justify-between gap-4 rounded-lg p-5 shadow-sm">
          <span className="text-base font-semibold text-gray-700">
            <AppFormattedMessage id="ROUND_DETAILS" />
          </span>
          <div className={twMerge('flex items-center gap-4')}>
            <FormInput
              control={control}
              defaultValue={
                snapshot?.valuation?.issuedSharesOnStart && snapshot?.valuation?.sharePrice
                  ? snapshot?.valuation?.issuedSharesOnStart * snapshot?.valuation?.sharePrice
                  : undefined
              }
              disabled={isPending}
              disabledInput={Boolean(pointerBlockedStyles)}
              isShownErrorMessage={false}
              name={'valuation.preMoneyValuation'}
              placeholder={<AppFormattedMessage id="VALUATION" />}
              shouldFormatNumber
              wrapperClassName={twMerge('min-w-[140px] max-w-[229px]', pointerBlockedStyles)}
            />
            <FormDatePicker
              calendar={{
                fromDate: scenario?.currentValuation ? new Date() : snapshot?.valuation?.date,
              }}
              disabled={isPending}
              disabledInput={Boolean(pointerBlockedStyles)}
              iconColor="#344054"
              inputValue={valuation?.date ? format(valuation?.date, 'dd/MM/yyyy') : ''}
              onSelect={(date) => {
                setValue('valuation.date', date);
              }}
              placeholder={<AppFormattedMessage id="EXPECTED_LIQUIDITY_DATE" />}
              value={valuation?.date && new Date(valuation?.date)}
              wrapperClassName={twMerge('max-w-[229px]', pointerBlockedStyles)}
            />

            <div
              className={twMerge(
                'flex h-[56px] w-[202px] items-center justify-center rounded-lg border border-gray-100 p-2',
                pointerBlockedStyles,
              )}
            >
              <div className="flex h-full w-full items-center gap-2 rounded-[4px] bg-gray-50 px-2">
                <Switch
                  checked={isSelectedAccelerationDate}
                  disabled={!isValuationReady}
                  onChange={(state) => {
                    setValue('accelerateVesting', state);
                  }}
                />
                <span className="text-nowrap text-sm font-[450] text-gray-700">
                  <AppFormattedMessage id="ACCELERATE_VESTING" />
                </span>
              </div>
            </div>
          </div>
        </div>
        {liquidityScenarioItems.map((item) => {
          const { description, key, hoverText, title, showButton, columns, columnsCount } = item;

          const itemsMap: Record<
            LiquidityScenarioKey,
            { itemsCount: number; selectedCount: null | number }
          > = {
            [LiquidityScenarioKey.CLASS_CONVERSION]: {
              itemsCount: (scenarioConversionTasks || [])?.length,
              selectedCount: classConversion?.classConversionItems?.length || 0,
            },
            [LiquidityScenarioKey.GRANTS]: {
              itemsCount: (scenarioVestingTasks || [])?.length,
              selectedCount: null,
            },
            [LiquidityScenarioKey.SAFES]: {
              itemsCount: toNumber(totalSafesCount),
              selectedCount: null,
            },
          };

          const isSelectedSafeCheck =
            safes &&
            selectedSafes &&
            safes.length === Object.keys(selectedSafes?.safeItems ?? {}).length;

          const isSelectedClassConversionCheck =
            scenarioConversionTasks &&
            classConversion &&
            scenarioConversionTasks.length ===
              Object.keys(classConversion?.classConversionItems ?? {}).length;

          return (
            <ScenarioDropdown
              companyId={companyId}
              control={control}
              description={description}
              hoverText={hoverText}
              isSelectChecked={
                key === LiquidityScenarioKey.CLASS_CONVERSION
                  ? isSelectedClassConversionCheck
                  : isSelectedSafeCheck
              }
              itemsCount={itemsMap[key].itemsCount}
              key={key}
              scenarioId={scenarioId}
              selectedCount={itemsMap[key].selectedCount}
              showButton={showButton && itemsMap[key].itemsCount > 0}
              title={title}
              type={key}
            >
              {scenarioFormMap[key]({
                columns,
                columnsCount,
                companyId,
                control,
                scenarioId,
                setValue,
                additionalColumns: (item as ExistingScenarioDetails).additionalColumns || [],
                additionalColumnsCount:
                  (item as ExistingScenarioDetails).additionalColumnsCount || 0,
                addButtonText: (item as NewScenarioDetails).addButtonText || '',
                scenarioType,
              })}
            </ScenarioDropdown>
          );
        })}
      </div>
    </>
  );
};
